import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const gap = 12; // Space between the element and the tooltip, including the arrow.

/** A hover tooltip. `hint` is a short text, or richer content (then the bubble gets more padding and left-aligned text). */
export const HoverHint: FC<{ children: ReactNode; hint: ReactNode }> = ({ children, hint }) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [below, setBelow] = useState(false);

  // The tooltip is rendered in a portal with fixed positioning, so it is not clipped by scrolling or overflow-hidden
  // ancestors (e.g. a table). That also means it doesn't follow its element, so it hides on scroll and resize.
  useEffect(() => {
    if (anchor === null) {
      return;
    }
    const hide = () => setAnchor(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [anchor]);

  // Shown above the element unless it doesn't fit there. Measured before the first paint, so there is no flicker.
  useLayoutEffect(() => {
    if (anchor !== null && tooltipRef.current) {
      setBelow(anchor.top < tooltipRef.current.offsetHeight + gap);
    }
  }, [anchor]);

  const show = () => {
    if (wrapperRef.current) {
      setBelow(false);
      setAnchor(wrapperRef.current.getBoundingClientRect());
    }
  };

  const style: CSSProperties | undefined =
    anchor === null
      ? undefined
      : {
          left: anchor.left + anchor.width / 2,
          ...(below
            ? { top: anchor.bottom }
            : { bottom: document.documentElement.clientHeight - anchor.top }),
        };
  const isRich = typeof hint !== "string";

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover-only hint, there is no action to expose
    <span
      ref={wrapperRef}
      className={"relative group"}
      onMouseEnter={show}
      onMouseLeave={() => setAnchor(null)}
    >
      {children}
      {anchor !== null &&
        createPortal(
          <span
            ref={tooltipRef}
            className={"fixed z-50 -translate-x-1/2 w-64 flex justify-center items-center pointer-events-none"}
            style={style}
          >
            <span
              className={`bg-slate-900 text-white text-xs rounded-md relative shadow-lg ring-1 ring-white/10 ${isRich ? "px-3 py-2.5 text-left w-full" : "px-2 py-1"} ${below ? "mt-2" : "mb-2"}`}
            >
              {hint}
              {/*Arrow*/}
              <div
                className={`absolute left-[50%] -translate-x-1/2 rotate-45 h-2 w-2 bg-slate-900 ${below ? "top-0 -translate-y-1/2" : "bottom-0 translate-y-1/2"}`}
              ></div>
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
};
